import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useThemeMode } from '@/contexts/ThemeContext'
import useIsMobile from '@/hooks/useIsMobile'
import { theme } from '@/lib/theme'
import { fetchWithAuth } from '@/lib/api/client'
import Button from './ui/Button'
import Message from './ui/Message'
import { LoadingText, loadingPresets } from '@/lib/loadingAnimations'

interface SubscriptionStatus {
  plan: 'free' | 'paid'
  status: string | null
  currentPeriodEnd: number | null
  cancelAtPeriodEnd: boolean
}

interface SubscriptionProps {
  onClose: () => void
  initialSuccessMessage?: string | null
}

/**
 * Subscription popup component for managing billing and subscription.
 */
export default function Subscription({ onClose, initialSuccessMessage }: SubscriptionProps) {
  const { colors } = useThemeMode()
  const isMobile = useIsMobile()
  const [isLoading, setIsLoading] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(initialSuccessMessage || null)
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [isFetchingSubscription, setIsFetchingSubscription] = useState(true)

  // Get font mode from localStorage to match the app's font setting
  const fontMode = typeof window !== 'undefined'
    ? (localStorage.getItem('kyogre-font-mode') as 'departure-mono' | 'open-dyslexic' | 'system' || 'departure-mono')
    : 'departure-mono'

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchSubscription = async () => {
      // Optimistic update for checkout success only (not portal return)
      const isCheckoutSuccess = initialSuccessMessage?.includes('Full Access Granted')

      if (isCheckoutSuccess) {
        setSubscription({
          plan: 'paid',
          status: 'active',
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
        })
        setIsFetchingSubscription(false)
        return
      }

      setIsFetchingSubscription(true)
      try {
        const url = `${import.meta.env.VITE_API_URL}/api/billing/subscription?refresh=true`
        const response = await fetchWithAuth(url)

        if (response.ok) {
          const data = await response.json()
          setSubscription(data)
        } else {
          console.error('Failed to fetch subscription:', response.status)
          // Default to free plan if fetch fails
          setSubscription({
            plan: 'free',
            status: null,
            currentPeriodEnd: null,
            cancelAtPeriodEnd: false,
          })
        }
      } catch (error) {
        console.error('Error fetching subscription:', error)
        // Default to free plan on error
        setSubscription({
          plan: 'free',
          status: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
        })
      } finally {
        setIsFetchingSubscription(false)
      }
    }

    fetchSubscription()
  }, [])

  const handleUpgrade = async () => {
    setIsLoading(true)
    setFeedbackMessage(null)
    try {
      const response = await fetchWithAuth(`${import.meta.env.VITE_API_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
      })

      if (response.ok) {
        const data = await response.json()
        // Redirect to Stripe checkout
        if (data.sessionUrl) {
          window.location.href = data.sessionUrl
        }
      } else {
        const errorData = await response.json()
        setFeedbackMessage(errorData.error || 'Error creating checkout session')
        setIsLoading(false)
      }
    } catch (error) {
      setFeedbackMessage('Error creating checkout session')
      console.error('Checkout error:', error)
      setIsLoading(false)
    }
  }

  const handleManageSubscription = async () => {
    setIsLoading(true)
    setFeedbackMessage(null)
    try {
      const response = await fetchWithAuth(`${import.meta.env.VITE_API_URL}/api/billing/create-portal-session`, {
        method: 'POST',
      })

      if (response.ok) {
        const data = await response.json()
        // Redirect to Stripe Customer Portal
        if (data.portalUrl) {
          window.location.href = data.portalUrl
        }
      } else {
        const errorData = await response.json()
        setFeedbackMessage(errorData.error || 'Error opening subscription portal')
        setIsLoading(false)
      }
    } catch (error) {
      setFeedbackMessage('Error opening subscription portal')
      console.error('Portal error:', error)
      setIsLoading(false)
    }
  }

  const currentPlan = subscription?.plan || 'free'
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing'

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '40px',
        backdropFilter: 'blur(8px)',
        fontFamily:
          fontMode === 'departure-mono'
            ? 'var(--font-departure-mono), monospace'
            : fontMode === 'open-dyslexic'
              ? 'var(--font-open-dyslexic), sans-serif'
              : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        style={{
          backgroundColor: colors.primaryBg,
          borderRadius: isMobile ? '8px' : '10px',
          border: `${isMobile ? '1px' : '2px'} solid ${colors.primaryBorder}`,
          padding: isMobile ? '14px' : '24px',
          maxWidth: '700px',
          width: isMobile ? '95%' : '90%',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: isMobile ? '10px' : '16px',
            right: isMobile ? '10px' : '16px',
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            color: colors.primaryText,
            cursor: 'pointer',
            fontSize: isMobile ? '14px' : '18px',
            padding: isMobile ? '4px 8px' : '6px 10px',
            lineHeight: '1',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.primaryBorder
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.secondaryBg
          }}
        >
          ✕
        </button>

        {/* Header */}
        <h2
          style={{
            fontSize: isMobile ? '14px' : '22px',
            fontWeight: '700',
            marginBottom: isMobile ? '12px' : '20px',
            color: colors.primaryText,
            letterSpacing: '-0.3px',
            paddingRight: '40px',
          }}
        >
          💳 Subscription & Billing
        </h2>

      {/* Loading Overlay */}
      {isFetchingSubscription && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            color: colors.secondaryText,
          }}
        >
          <LoadingText {...loadingPresets.loading('Loading subscription...', colors.secondaryText)} />
        </div>
      )}

      {!isFetchingSubscription && (
        <>
          {feedbackMessage && (
            <Message
              variant={feedbackMessage.startsWith('Error') || feedbackMessage.includes('failed') ? 'error' : 'success'}
              style={{ marginBottom: theme.spacing.lg }}
            >
              {feedbackMessage}
            </Message>
          )}

          {/* Current Plan Section */}
          <div
            style={{
              backgroundColor: colors.secondaryBg,
              border: `2px solid ${colors.primaryBorder}`,
              borderRadius: '8px',
              padding: isMobile ? '14px' : '18px',
              marginBottom: isMobile ? '12px' : '16px',
            }}
          >
            <h3
              style={{
                fontSize: isMobile ? '13px' : '15px',
                fontWeight: '700',
                marginBottom: isMobile ? '10px' : '12px',
                color: colors.primaryText,
                letterSpacing: '-0.2px',
              }}
            >
              Current Plan
            </h3>
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? '12px' : '16px',
              }}
            >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <p
                style={{
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '700',
                  color: currentPlan === 'paid'
                    ? (subscription?.cancelAtPeriodEnd
                        ? 'var(--subscription-badge-inactive-text)'
                        : (isActive ? 'var(--subscription-paid-text)' : 'var(--subscription-badge-inactive-text)'))
                    : 'var(--fg)',
                  margin: 0,
                  letterSpacing: '-0.3px',
                }}
              >
                {currentPlan === 'paid' ? 'Full Access' : 'Free Plan'}
              </p>
              {/* Status Badge for Paid Plans */}
              {currentPlan === 'paid' && subscription?.status && (
                <span
                  style={{
                    fontSize: isMobile ? '10px' : '11px',
                    fontWeight: '600',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    backgroundColor: subscription.cancelAtPeriodEnd
                      ? 'var(--subscription-badge-inactive-bg)'
                      : (isActive
                          ? 'var(--subscription-badge-active-bg)'
                          : 'var(--subscription-badge-inactive-bg)'),
                    color: subscription.cancelAtPeriodEnd
                      ? 'var(--subscription-badge-inactive-text)'
                      : (isActive
                          ? 'var(--subscription-badge-active-text)'
                          : 'var(--subscription-badge-inactive-text)'),
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {subscription.cancelAtPeriodEnd ? 'CANCELLED' : subscription.status}
                </span>
              )}
            </div>

            {/* Show price for free plan or active paid plan */}
            {(!subscription?.cancelAtPeriodEnd || currentPlan === 'free') && (
              <p
                style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: colors.secondaryText,
                  margin: '4px 0 0 0',
                }}
              >
                {currentPlan === 'paid' ? '$20.00 / month' : '$0.00 / month'}
              </p>
            )}

            {/* Show expiration date for cancelled subscriptions */}
            {subscription?.cancelAtPeriodEnd && subscription?.currentPeriodEnd && (
              <p
                style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: colors.visualAccent1,
                  margin: '4px 0 0 0',
                }}
              >
                expires on: {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}
              </p>
            )}
            {subscription?.currentPeriodEnd && !subscription?.cancelAtPeriodEnd && (
              <p
                style={{
                  fontSize: isMobile ? '11px' : '12px',
                  color: colors.secondaryText,
                  margin: '4px 0 0 0',
                }}
              >
                next billing cycle: {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}
              </p>
            )}
          </div>
          {currentPlan === 'free' ? (
            <Button
              onClick={handleUpgrade}
              disabled={isLoading || isFetchingSubscription}
              isLoading={isLoading}
              variant="primary"
              size={isMobile ? 'sm' : 'md'}
              style={isMobile ? { alignSelf: 'flex-start' } : undefined}
            >
              Upgrade to Full Access
            </Button>
          ) : (
            <Button
              onClick={handleManageSubscription}
              disabled={isLoading || isFetchingSubscription}
              isLoading={isLoading}
              variant="secondary"
              size={isMobile ? 'sm' : 'md'}
              style={isMobile ? { alignSelf: 'flex-start' } : undefined}
            >
              {subscription?.cancelAtPeriodEnd ? 'Reactivate' : 'Manage Subscription'}
            </Button>
          )}
        </div>
      </div>

      {/* Plan Features */}
      <div
        style={{
          backgroundColor: colors.secondaryBg,
          border: `2px solid ${colors.primaryBorder}`,
          borderRadius: '8px',
          padding: '18px',
          marginBottom: '16px',
        }}
      >
        <h3
          style={{
            fontSize: '15px',
            fontWeight: '700',
            marginBottom: '12px',
            color: colors.primaryText,
            letterSpacing: '-0.2px',
          }}
        >
          {currentPlan === 'free' ? 'Your Current Features' : 'Full Access Features'}
        </h3>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
          }}
        >
          {currentPlan === 'free' ? (
            <>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                Basic chat functionality
              </li>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                <span>
                  <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>20 messages per day</span>
                  {' → '}
                  <span style={{ color: 'var(--subscription-paid-text)', fontWeight: '600' }}> ✨ 1,000 messages per day with Full Access</span>
                </span>
              </li>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                File explorer access
              </li>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                Profile & theme customization
              </li>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                Create Identities
              </li>
              
            </>
          ):
          (
            <>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                Basic chat functionality
              </li>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                1,000 messages per day 
              </li>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                File explorer access
              </li>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                Profile & theme customization
              </li>
              <li
                style={{
                  fontSize: '13px',
                  color: colors.primaryText,
                  marginBottom: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ color: colors.visualAccent1, fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                Create Identities
              </li>
              
            </>
          )}
        </ul>
      </div>
        </>
      )}
    </div>
    </div>,
    document.body
  )
}
